import { observer } from 'mobx-react-lite'
import { Platform, StyleSheet, TouchableOpacity, StatusBar as StatusBarRN } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { DS } from '../../design-system'
import { useThrottledCallback } from 'use-debounce'
import { StatusBarStyle, StatusBar } from 'expo-status-bar'
import { ScheduledGameCard } from '../../components/ScheduledGameCard'
import { useStore } from '../../models'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { events } from '../../models/analytics/analytics-events'
import { DeleteBottomSheetModal } from '../create-tournament-screen/DeleteBottomSheetModal'
import { useRouter } from 'expo-router'

export const AceptInviteScreen = observer(({ code }: { code: string }) => {
  const router = useRouter()
  const {
    session: { user },
    game: {
      formattedGameScheduleFullResponse,
      userGameScheduleFullResponse,
      queryUserGameScheduleByHash,
      createDeleteInviteSheet
    },
    api: { mutateAcceptInviteToScheduleWithHash },
    analytics
  } = useStore()

  const [isLoading, setIsLoading] = useState(true)
  const inviteList = useMemo(() => userGameScheduleFullResponse?.usersToNotify ?? [], [userGameScheduleFullResponse])
  const userId = user?._id.toString() ?? ''

  const isMyUserInInviteList = useMemo(() => inviteList.includes(userId), [inviteList, userId])
  const isHost = userGameScheduleFullResponse?.isHost

  const showAddGame = useMemo(() => {
    // Don't show if i'm the host
    if (isHost) {
      return false
    }
    return !(isMyUserInInviteList || isLoading)
  }, [isMyUserInInviteList, isLoading, isHost])

  const showRemoveGame = useMemo(() => isMyUserInInviteList && !isLoading, [isMyUserInInviteList, isLoading])

  const fetchInviteDetails = useCallback(async () => {
    // if (!hash || userGameScheduleFullResponse) {
    //   return
    // }
    setIsLoading(true)

    try {
      await queryUserGameScheduleByHash(code)
    } catch (e) {
      console.log('LOG: > e:', e)
      router.push(`/(root)/game/join/${code}`)
    } finally {
      setIsLoading(false)
    }
  }, [queryUserGameScheduleByHash, code])

  useEffect(() => {
    fetchInviteDetails()
  }, [fetchInviteDetails])

  const onBack = useThrottledCallback(
    () => {
      if (router.canDismiss()) {
        return router.dismiss()
      }
      if (router.canGoBack()) {
        return router.back()
      }
      router.dismissTo('/(root)/(tabs)/play')
    },
    350,
    {
      leading: true
    }
  )

  const handleAddGameInvitation = async () => {
    if (userGameScheduleFullResponse?.hash) {
      try {
        await mutateAcceptInviteToScheduleWithHash({ hash: userGameScheduleFullResponse.hash })
        router.dismissTo('/(root)/(tabs)/play')

        analytics.setEvent(events.game.schedule_game_added, {
          game_name: formattedGameScheduleFullResponse.name
        })
      } catch (error) {
        console.error(error)
        router.push(`/(root)/game/join/${code}`)
      }
    } else if (code) {
      router.push(`/(root)/game/join/${code}`)
    } else {
      router.dismissTo('/(root)/(tabs)/play')
    }
  }

  const triggerDeleteInvitationSheet = useThrottledCallback(
    () => {
      createDeleteInviteSheet()
    },
    350,
    {
      leading: true
    }
  )

  return (
    <SafeAreaView style={styles.screen}>
      <StatusBar animated={true} style={'light' as StatusBarStyle} hidden={false} />
      <DS.Frame style={styles.container}>
        <DS.Frame gap="24" style={{ paddingTop: 24 }}>
          <DS.Frame row justifyContent="space-between" alignItems="center">
            <TouchableOpacity onPress={onBack}>
              <DS.Icon name="Close" size={24} color="Global/White" />
            </TouchableOpacity>
            {showRemoveGame && (
              <TouchableOpacity onPress={triggerDeleteInvitationSheet}>
                <DS.Text variant="Body Medium/Regular" color="Global/White">
                  Remove Game
                </DS.Text>
              </TouchableOpacity>
            )}
          </DS.Frame>
          <DS.Text variant="Heading 1/Medium" color="Brand/Blue">
            Tournament
          </DS.Text>

          {userGameScheduleFullResponse ? <ScheduledGameCard {...formattedGameScheduleFullResponse} /> : null}
        </DS.Frame>

        {showAddGame && (
          <DS.Button
            onPress={handleAddGameInvitation}
            variant="Large/Primary"
            type="On Dark"
            style={{ position: 'relative', bottom: 0 }}
          >
            Add Game
          </DS.Button>
        )}
      </DS.Frame>
      <DeleteBottomSheetModal />
    </SafeAreaView>
  )
})

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Purple'],
    paddingTop: Platform.OS === 'android' ? StatusBarRN.currentHeight : 0
  },
  container: {
    paddingBottom: DS.spacing['16'],
    paddingHorizontal: 24,
    flex: 1,
    justifyContent: 'space-between'
  }
})
